/**
 * DraftingMatterPickerModal - rich matter picker rendered inside GlobalModal.
 *
 * The header (title / subMsg / icon / close button) is rendered by GlobalModal
 * itself using the standard `message`, `subMsg`, `icon` props on `showModal`.
 * This component renders only the body (search + card list + footer actions),
 * wrapped in horizontal padding to match other GlobalModal children.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { PiBriefcaseLight, PiCheckCircleLight } from 'react-icons/pi';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector, RootState } from '../../store';
import { addDraftingAttachment, DraftingKind } from '../../store/slices/draftingSlice';
import { fetchMatters } from '../../store/actions/Matters/MatterActions';
import { hideModal } from '../../store/actions/modalActions';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import Button from '../shared/TailwindComponents/Button';
import SearchInput from '../shared/TailwindComponents/SearchInput';

// Stable empty-array reference so the selector never produces a new array
// identity for the "matters not yet loaded" case (which would cause an
// infinite re-render loop through useSelector reference equality).
const EMPTY_MATTERS: any[] = [];

interface DraftingMatterPickerModalProps {
  draftingKind: DraftingKind;
  draftingUuid: string;
  existingMatterIds: number[];
  onAttached?: () => void;
}

function statusColor(status: string): string {
  const s = status.toLowerCase();
  if (s.includes('progress') || s === 'active' || s === 'open') return 'text-success bg-success/10';
  if (s === 'closed' || s === 'archived' || s === 'completed') return 'text-gray-500 bg-gray-100 dark:bg-gray-700';
  if (s === 'pending' || s.includes('hold')) return 'text-warning bg-warning/10';
  return 'text-info bg-info/10';
}

function SkeletonCards() {
  return (
    <div className="space-y-2">
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className="glass rounded-lg px-4 py-3"
        >
          <div className="flex items-center gap-3">
            <div className="h-5 w-5 flex-shrink-0 rounded bg-gray-200 dark:bg-gray-700 animate-fadePulse" />
            <div className="flex-1 space-y-2">
              <div className="h-3.5 w-2/3 rounded bg-gray-200 dark:bg-gray-700 animate-fadePulse" />
              <div className="h-3 w-1/3 rounded bg-gray-100 dark:bg-gray-800 animate-fadePulse" />
            </div>
            <div className="h-5 w-14 rounded-full bg-gray-100 dark:bg-gray-800 animate-fadePulse" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function DraftingMatterPickerModal({
  draftingKind,
  draftingUuid,
  existingMatterIds,
  onAttached,
}: Readonly<DraftingMatterPickerModalProps>) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const matters = useAppSelector(
    (s: RootState) => (s.matters?.matters as any[] | undefined) ?? EMPTY_MATTERS,
  );

  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [attaching, setAttaching] = useState(false);
  // Always start loading - the picker ALWAYS does a full paginated fetch on
  // open (see below), so we can't trust whatever partial list is already in
  // the store.
  const [loading, setLoading] = useState(true);

  // One-time fetch on mount, guarded by a ref against StrictMode
  // double-invocation. NOTE: do NOT gate the loading flip on a per-effect
  // `cancelled` flag - under StrictMode the first effect's cleanup sets it
  // true, the re-run bails on `didFetch`, and the in-flight fetch's finally
  // would then skip setLoading(false), leaving the modal stuck on the
  // skeleton forever. Always flip loading off when the fetch settles.
  //
  // We do NOT early-return when the store already has matters: another view
  // (e.g. the matters list) may have loaded only the first page (12), which
  // would cap the picker. Always fetch every page here.
  const didFetch = useRef(false);
  useEffect(() => {
    if (didFetch.current) return;
    didFetch.current = true;
    setLoading(true);
    (async () => {
      try {
        // Fetch ALL matters so the picker isn't capped at one page. Page 1
        // replaces the store list (FETCH_ALL_MATTERS); subsequent pages append
        // (APPEND_MATTERS). Sort is enforced client-side below regardless.
        const PAGE_SIZE = 100;
        const first: any = await dispatch(
          fetchMatters({ page: 1, page_size: PAGE_SIZE, sortBy: 'created_at', sortOrder: 'desc' }),
        );
        const total = Number(first?.total_matters ?? 0);
        const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
        for (let p = 2; p <= totalPages; p++) {
          await dispatch(
            fetchMatters({ page: p, page_size: PAGE_SIZE, sortBy: 'created_at', sortOrder: 'desc' }),
          );
        }
      } catch {
        // Errors are surfaced through the global error toast already.
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const excludedIds = useMemo(
    () => new Set(existingMatterIds.map(Number)),
    [existingMatterIds],
  );

  const createdMs = (m: any): number => {
    const raw = m?.created_at || m?.createdAt || 0;
    const ms = new Date(raw).getTime();
    return Number.isNaN(ms) ? 0 : ms;
  };

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return matters
      .filter((m: any) => !excludedIds.has(Number(m.id)))
      .slice()
      .sort((a: any, b: any) => createdMs(b) - createdMs(a)) // newest created first
      .filter((m: any) => {
        if (!q) return true;
        const title = String(m.title || '').toLowerCase();
        const type = String(m.type || '').toLowerCase();
        const status = String(m.status_name || '').toLowerCase();
        const jurisdiction = String(m.jurisdiction || '').toLowerCase();
        const description = String(m.description || '').toLowerCase();
        const clients = Array.isArray(m.matter_clients?.clients)
          ? m.matter_clients.clients.map((c: any) => String(c?.name || '').toLowerCase()).join(' ')
          : '';
        return (
          title.includes(q) ||
          type.includes(q) ||
          status.includes(q) ||
          jurisdiction.includes(q) ||
          description.includes(q) ||
          clients.includes(q)
        );
      });
  }, [matters, query, excludedIds]);

  const handleAttach = async () => {
    if (!selectedId) return;
    setAttaching(true);
    try {
      const result = await dispatch(
        addDraftingAttachment({
          kind: draftingKind,
          uuid: draftingUuid,
          entity_type: 'matter',
          entity_id: selectedId,
        }),
      );
      if (addDraftingAttachment.rejected.match(result)) {
        dispatch(
          showFeedbackModal({
            modalType: 'error',
            message: t('Failed to attach matter'),
            showModal: true,
            duration: 3000,
          }),
        );
        return;
      }
      onAttached?.();
      dispatch(hideModal());
    } finally {
      setAttaching(false);
    }
  };

  const renderBody = (): React.ReactNode => {
    if (loading) return <SkeletonCards />;
    if (visible.length === 0) {
      return (
        <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
          {matters.length === 0
            ? t('No matters available to attach.')
            : t('No matters match your search.')}
        </p>
      );
    }
    return (
      <div className="space-y-2">
        {visible.map((m: any) => {
          const matterId = Number(m.id);
          const isSelected = selectedId === matterId;
          const title = m.title || `Matter #${m.id}`;
          const type = m.type || null;
          const status = m.status_name || null;
          const stage = m.stage_name || null;
          const jurisdiction = m.jurisdiction || null;
          const clientsArr: any[] = Array.isArray(m.matter_clients?.clients)
            ? m.matter_clients.clients
            : [];
          const clientNames = clientsArr
            .map((c) => c?.name)
            .filter(Boolean)
            .join(', ');
          const description = typeof m.description === 'string' ? m.description : '';
          const descriptionPreview =
            description.length > 140 ? `${description.slice(0, 140).trim()}…` : description;

          return (
            <button
              key={m.id}
              type="button"
              onClick={() => setSelectedId(isSelected ? null : matterId)}
              className={`group w-full rounded-lg border px-4 py-3 text-start transition-all ${
                isSelected
                  ? 'border-primary-500 bg-primary-50 dark:border-primary-400 dark:bg-primary-900/20'
                  : 'border-gray-200 bg-white hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-start gap-3">
                  <PiBriefcaseLight
                    size={18}
                    className={`mt-0.5 flex-shrink-0 transition-colors ${
                      isSelected
                        ? 'text-primary-600 dark:text-primary-300'
                        : 'text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300'
                    }`}
                  />
                  <div className="min-w-0">
                    <p
                      className={`truncate text-sm font-semibold ${
                        isSelected
                          ? 'text-primary-700 dark:text-primary-200'
                          : 'text-gray-900 dark:text-white'
                      }`}
                    >
                      {title}
                    </p>

                    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                      {type && (
                        <span className="text-xs text-gray-600 dark:text-gray-400">{type}</span>
                      )}
                      {stage && (
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          • {stage}
                        </span>
                      )}
                      {jurisdiction && (
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          • {jurisdiction}
                        </span>
                      )}
                      {clientNames && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          • {clientNames}
                        </span>
                      )}
                    </div>

                    {descriptionPreview && (
                      <p className="mt-1.5 line-clamp-2 text-xs text-gray-500 dark:text-gray-400">
                        {descriptionPreview}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex flex-shrink-0 items-center gap-2">
                  {status && (
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize ${statusColor(
                        status,
                      )}`}
                    >
                      {status}
                    </span>
                  )}
                  {isSelected && (
                    <PiCheckCircleLight
                      size={18}
                      className="text-primary-600 dark:text-primary-300"
                    />
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div className="flex w-[560px] max-w-full flex-col gap-4 px-6 pb-2 pt-2">
      <SearchInput
        value={query}
        onChange={setQuery}
        placeholder={t('Search by title, client or reference')}
      />

      <div className="max-h-[420px] min-h-[200px] overflow-y-auto pe-1">{renderBody()}</div>

      <div className="flex items-center justify-between border-t border-gray-200 pt-4 dark:border-gray-700">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {!loading && visible.length > 0 ? `${visible.length} ${t('available')}` : ''}
        </p>
        <div className="flex gap-2">
          <Button variant="neutral" size="small" onClick={() => dispatch(hideModal())}>
            {t('Cancel')}
          </Button>
          <Button
            variant="primary"
            size="small"
            onClick={handleAttach}
            disabled={!selectedId || attaching}
            isLoading={attaching}
          >
            {t('Attach matter')}
          </Button>
        </div>
      </div>
    </div>
  );
}
